import { Injectable } from '@angular/core';
import { DataService } from "../../../core/services/data.service";
import { ProtoService } from '../../../core/services/proto.service';
import { ServerService } from '../../../core/services/server.service';

import * as protos from "../../../../assets/proto/compiled/all";
import { AuthService } from '../../../core/services/auth.service';
import { MenuEditorUtil } from '../util/menu-editor-utils';
import { LocalizationService } from '../../../core/services/localization.service';
import { Formatter } from '../../../util/formatter';
import { TranslateService } from '@ngx-translate/core';

import { PopupsService } from '../../../core/services/popups.service';
import { BehaviorSubject, Observable, Observer, Subject, firstValueFrom, lastValueFrom } from 'rxjs';
import { AggregatedSelection, BaseProtoResponse, Consumer, EditorCategory, MenuHistory, MenuLoadResult, Money } from '../util/interfaces';
import { ProtoUtil } from '../../../util/proto-util';
import { Util } from '../../../util/util';

@Injectable({
  providedIn: 'root'
})
export class MenuEditorService {

  private businessActive: boolean = true;
  private menuHistory = new BehaviorSubject<MenuHistory>({
    activeIndex: 0,
    versions: []
  });
  private timeTravelTrigger = new Subject<number>();

  private textProtoSchema = {
    type: "object",
    properties: {
      text: { type: "string" },
      language: { type: "number" }
    },
    required: ["text", "language"]
  };

  private priceProtoSchema = {
    type: "object",
    properties: {
      currencyCode: { type: "string" },
      units: { type: "number" },
      cents: { type: "number" }
    },
    required: ["currencyCode", "units"]
  };

  constructor(private serverSrv: ServerService,
              private protoSrv: ProtoService,
              private dataSrv: DataService,
              private authSrv: AuthService,
              private translate: TranslateService,
              private popupsSrv: PopupsService) 
  {
  }

  public moneyToSimpleString(money: Money | null | undefined): string {
    if (!money) {
      return '';
    }
    let str = money.units + '.';
    if (!money.cents || money.cents == 0) {
      str += '00';
    } else if(money.cents < 10) {
      str += '0' + money.cents;
    } else {
      str += money.cents;
    }
    return str;
  }

  public moneyToString(money: Money | null | undefined): string {
    if (!money) {
      return '';
    }
    let str = '$' + this.moneyToSimpleString(money);
    return str + ' ' + money.currencyCode;
  }

  public stringToMoney(strMoney: string | null | undefined): Money | null | undefined {
    if (!strMoney) {
      return null;
    }

    const moneyComponents = strMoney.trim().replace('$', '').split(' ');
    if (moneyComponents.length != 2) {
      // this.loggingService.logError('Invalid string money: ' + strMoney);
      return null;
    }

    const amountComponents = moneyComponents[0].split('.');
    if (amountComponents.length != 2) {
      // this.loggingService.logError('Invalid string money: ' + strMoney);
      return null;
    }

    let units: number = 0;
    let cents: number = 0;
    let currencyCode: string = '';

    let strCents = amountComponents[1];
    if (strCents.length == 1) {
      strCents += '0';
    }

    units = Number(amountComponents[0]);
    cents = Number(strCents);
    currencyCode = moneyComponents[1];

    if (isNaN(units) || isNaN(cents)) {
      // this.loggingService.logError('Invalid string money: ' + strMoney);
      return null;
    }

    if (currencyCode.trim().length == 0) {
      // this.loggingService.logError('Invalid string money: ' + strMoney);
      return null;
    }

    return {units: units, cents:cents, currencyCode: currencyCode};
  }

  public get menuSpecSchema() {
    return{
      type: "object",
      properties: {
        categories: {
          type: "array",
          items: {
            type: "object",
            properties: {
              id: { type: "string" },
              name: this.textProtoSchema
            },
            required: ["id", "name"]
          }
        },
        items: {
          type: "array",
          description: "Reusable ingredients or simple options (Tomato, Onion, Soda Flavor, etc.)",
          items: {
            type: "object",
            properties: {
              id: { type: "string" },
              name: this.textProtoSchema,
              price: this.priceProtoSchema
            },
            required: ["id", "name"]
          }
        },
        itemSelections: {
          type: "array",
          description: "Groups of items (e.g., 'Meat Choice' containing IDs for Beef, Chicken)",
          items: {
            type: "object",
            properties: {
              id: { type: "string" },
              title: this.textProtoSchema,
              itemIds: { type: "array", items: { type: "string" } },
              selectionType: { 
                type: "number", 
                description: "Numeric value ONLY: 1 for SINGLE, 2 for MULTIPLE" 
              },
              isRequired: { type: "boolean" },
              maxSelections: { type: "number" }
            },
            required: ["id", "title", "itemIds", "selectionType"]
          }
        },
        menuItems: {
          type: "array",
          description: "The main dishes sold in the menu",
          items: {
            type: "object",
            properties: {
              id: { type: "string" },
              name: this.textProtoSchema,
              description: this.textProtoSchema,
              price: this.priceProtoSchema,
              categoryIds: { type: "array", items: { type: "string" } },
              itemSelectionIds: { type: "array", items: { type: "string" } },
              removableIngredientItemIds: { type: "array", items: { type: "string" } },
              station: { type: "string" },
              calories: { type: "number" },
              containsAlcohol: { type: "boolean" }
            },
            required: ["id", "name", "price", "categoryIds"]
          }
        }
      }
    };
  }

  // ================================== TEXT UTIL ==================================
  public getMenuEntryName(menuEntry: protos.waiternow.common.MenuProto.IMenuEntryProto, localizationService: LocalizationService): string {
    if (menuEntry?.menuItem?.name) {
      return this.getNonEmptyText(menuEntry.menuItem.name, localizationService);
    } else if (menuEntry?.combo) {
      return this.getNonEmptyText(menuEntry.combo.name, localizationService);
    }
    return '';
  }

  public getMenuEntryPrice(menuEntry: protos.waiternow.common.MenuProto.IMenuEntryProto): string {
    if (menuEntry?.menuItem) {
      return Formatter.formatMoney(menuEntry?.menuItem?.price);
    } else if (menuEntry?.combo) {
      return Formatter.formatMoney(menuEntry?.combo?.price);
    }
    return '';
  }

  public getMenuEntryDescription(menuEntry: protos.waiternow.common.MenuProto.IMenuEntryProto, localizationService: LocalizationService): string {
    if (menuEntry?.menuItem?.description) {
      return this.getNonEmptyText(menuEntry?.menuItem?.description, localizationService);
    } else if (menuEntry?.combo?.description) {
      return this.getNonEmptyText(menuEntry?.combo?.description, localizationService);
    }
    return '';
  }

  public getMenuEntryImageUrl(menuEntry: protos.waiternow.common.MenuProto.IMenuEntryProto): string {
    if (menuEntry?.menuItem?.image?.imageUrl) {
      return menuEntry.menuItem.image.imageUrl;
    } else if (menuEntry?.combo?.image?.imageUrl) {
      return menuEntry.combo.image.imageUrl;
    }
    return 'assets/icon/no-image-placeholder.jpg';
  }

  public hasMenuEntryImage(menuEntry: protos.waiternow.common.MenuProto.IMenuEntryProto): boolean {
    if (menuEntry?.menuItem?.image?.imageUrl) {
      return true;
    } else if (menuEntry?.combo?.image?.imageUrl) {
      return true;
    }
    return false;
  }

  public getNonEmptyText(textProto: protos.waiternow.common.ITextProto | null | undefined, localizationService: LocalizationService): string {
    return MenuEditorUtil.getNonEmptyText(textProto, localizationService);
  }

  // ================================ PROTO SERVICE =================================

  public setMenuFromDraft(proto: any) {
    var menuSpec = this.protoSrv.getMenuFromGetDraftOfMenuProto(proto);
    if (!menuSpec){
      menuSpec = this.protoSrv.createEmptyMenuSpecProto();
    }
    return menuSpec;
  }

  public denormalizeMenu(menu: protos.waiternow.common.MenuSpecProto) {
    const protobytes = this.protoSrv.createDenormalizeMenuRequest(menu); // create proto
    return this.serverSrv.postBin('denormalizestructuredmenu', protobytes, this.authSrv.getAuthToken(), '');
  }

  public decodeDenormalizeMenuProtoResponse(answer: any) {
    return this.protoSrv.decodeDenormalizeMenuProtoResponse(answer);
  }

  public isDenormalizedMenuProtoResponseSuccess(answer: any) {
    return this.protoSrv.isDenormalizedMenuProtoResponseSuccess(answer);
  }

  public getMenuProto() {
    this.businessActive = this.dataSrv.getBusinessActive();
    const protobytes = this.protoSrv.createGetDrafOfMenuProto(this.businessActive ? this.dataSrv.getBusinessId() : '',
                                                                        this.businessActive ? '' : this.dataSrv.getLocationId()); // create proto
    return this.serverSrv.postBin('getdraft', protobytes, this.authSrv.getAuthToken(), '');
  }

  public decodeGetDraftOfMenuProto(buffer: ArrayBuffer) {
    return this.protoSrv.decodeGetDraftOfMenuProto(buffer);
  }

  public isGetDraftOfMenuProtoSuccess(proto: any) {
    return this.protoSrv.isGetDraftOfMenuProtoSuccess(proto);
  }

  public publishDraftMenuProto(menuSpec: protos.waiternow.common.MenuSpecProto) {
    this.businessActive = this.dataSrv.getBusinessActive();
    const protobytes = this.protoSrv.createPublishDrafOfMenuProto(this.businessActive ? this.dataSrv.getBusinessId() : '',
                                                                           this.businessActive ? '' : this.dataSrv.getLocationId(),
                                                                           menuSpec);
    return this.serverSrv.postBin('publishdraft', protobytes, this.authSrv.getAuthToken(), '');
  }

  public decodePublishDraftOfMenuProto(buffer: ArrayBuffer) {
    return this.protoSrv.decodePublishDraftOfMenuProto(buffer);
  }

  public isPublishDraftOfMenuProtoSuccess(response: any) {
    return this.protoSrv.isPublishDraftOfMenuProtoSuccess(response);
  }

  public saveDraftMenuProto(menuSpec: protos.waiternow.common.MenuSpecProto) {
    this.businessActive = this.dataSrv.getBusinessActive();
    const protobytes = this.protoSrv.createSaveDrafOfMenuProto(this.businessActive ? this.dataSrv.getBusinessId() : '',
                                                                         this.businessActive ? '' : this.dataSrv.getLocationId(),
                                                                         menuSpec); // create proto
    return this.serverSrv.postBin('savedraft', protobytes, this.authSrv.getAuthToken(), '');
  }

  public decodeSaveDraftOfMenuProto(buffer: ArrayBuffer) {
    return this.protoSrv.decodeSaveDraftOfMenuProto(buffer);
  }

  public isSaveDraftOfMenuProtoSuccess(answer: any) {
    return this.protoSrv.isSaveDraftOfMenuProtoSuccess(answer);
  }

  public removeDraftMenuProto(menuSpec: protos.waiternow.common.MenuSpecProto) {
    this.businessActive = this.dataSrv.getBusinessActive();
    const protobytes = this.protoSrv.createRemoveDrafOfMenuProto(this.businessActive ? this.dataSrv.getBusinessId() : '',
                                                                           this.businessActive ? '' : this.dataSrv.getLocationId(),
                                                                           menuSpec); // create proto
    return this.serverSrv.postBin('removedraft', protobytes, this.authSrv.getAuthToken(), '');
  }

  public decodeRemoveDraftOfMenuProto(buffer: ArrayBuffer) {
    return this.protoSrv.decodeRemoveDraftOfMenuProto(buffer);
  }

  public isRemoveDraftOfMenuProtoSuccess(answer: any) {
    return this.protoSrv.isRemoveDraftOfMenuProtoSuccess(answer);
  }

  public removeMenuProto() {
    this.businessActive = this.dataSrv.getBusinessActive();
    const protobytes = this.protoSrv.createRemoveMenuProto(this.businessActive ? this.dataSrv.getBusinessId() : '',
                                                  this.businessActive ? '' : this.dataSrv.getLocationId()); // create proto
    return this.serverSrv.postBin('removestructuredmenu', protobytes, this.authSrv.getAuthToken(), '');
  }

  public decodeRemoveMenuProto(buffer: ArrayBuffer) {
    return this.protoSrv.decodeRemoveMenuProto(buffer);
  }

  isRemoveMenuProtoSuccess(answer: any) {
    return this.protoSrv.isRemoveMenuProtoSuccess(answer);
  }

  addImageToMenu(image: Uint8Array) {
    let businessId = '';
    businessId = this.dataSrv.getBusinessId();
    const protobytes = this.protoSrv.createAddImageToMenuProto(this.businessActive ? this.dataSrv.getBusinessId() : '',
                                                                         this.businessActive ? '' : this.dataSrv.getLocationId(),
                                                                         image); // create proto
    return this.serverSrv.postBin('addstructuredmenuimage', protobytes, this.authSrv.getAuthToken(), '');
  }

  decodeAddImageToMenuProto(buffer: ArrayBuffer) {
    return this.protoSrv.decodeAddImageToMenuProto(buffer);
  }

  isAddImageToMenuProtoSuccess(proto: protos.waiternow.common.AddImageToMenuActionProto.Response) {
    return this.protoSrv.isAddImageToMenuProtoSuccess(proto);
  }

  getIdFromAddImageToMenuProto(proto: protos.waiternow.common.AddImageToMenuActionProto.Response) {
    return this.protoSrv.getIdFromAddImageToMenuProto(proto);
  }

  createImageProto(imageId: string, imageUrl: string) {
    return this.protoSrv.createImageProto(imageId, imageUrl);
  }

  createGetGoogleApiKeysActionProtoRequest() {
    const protobytes = this.protoSrv.createGetGoogleApiKeysActionProtoRequest();
    return this.serverSrv.postBin('getgoogleapikey', protobytes, this.authSrv.getAuthToken(), '');
  }

  decodeGetGoogleApiKeysActionProtoResponse(buffer: ArrayBuffer) {
    return this.protoSrv.decodeGetGoogleApiKeysActionProtoResponse(buffer);
  }

  isGetGoogleApiKeysActionProtoResponseSuccess(response: protos.waiternow.common.GetGoogleApiKeysActionProto.Response) {
    return this.protoSrv.isGetGoogleApiKeysActionProtoResponseSuccess(response);
  }

  getGoogleApiKeysFromProtoResponse(response: protos.waiternow.common.GetGoogleApiKeysActionProto.Response) {
    return this.protoSrv.getGoogleApiKeysFromProtoResponse(response);
  }

  public async getGoogleApiKey() {
    try {
      const data = await lastValueFrom(this.createGetGoogleApiKeysActionProtoRequest());
      const answer = this.decodeGetGoogleApiKeysActionProtoResponse(data);

      if (this.isGetDraftOfMenuProtoSuccess(answer)) {
        const googleAiServicesApiKey = this.getGoogleApiKeysFromProtoResponse(answer);
        this.setGoogleAiServicesApiKey(googleAiServicesApiKey);
        return googleAiServicesApiKey;
      } else {
        this.popupsSrv.dismissLoading();
        const msgText: string = this.translate.instant("ErrorFetchingGoogleApiKey");
        this.popupsSrv.presentToast(msgText, 2500, 'middle');
        throw new Error(msgText);
      }
    } catch (err: any) {
      this.popupsSrv.dismissLoading();
      console.error("Error details: ", err);
      throw err;
    }
  }

  // ================================ DATA SERVICE =================================
  getBusinessActive() {
    return this.dataSrv.getBusinessActive();
  }

  getBusinessName() {
    return this.dataSrv.getBusinessName();
  }

  getLocationName() {
    return this.dataSrv.getLocationName();
  }

  getGoogleAiServicesApiKey() {
    return this.dataSrv.getGoogleAiServicesApiKey();
  }

  setGoogleAiServicesApiKey(key: string) {
    this.dataSrv.setGoogleAiServicesApiKey(key);
  }

  // ================================ MENU EDITOR =================================
  public saveDraft(
    flagLoading: boolean = true,
    menuSpec: protos.waiternow.common.MenuSpecProto
  ) {
    if(flagLoading == true) {
      this.translate.get("Loading").subscribe((text) => {
        this.popupsSrv.presentLoading(text).then(() => {
        });
      });
    }
    this.saveAsyncDraft(menuSpec).subscribe({
      next: () => {
        const savedMessage = flagLoading ? 'SavedDraft' : 'AutoSavedDraft';
        this.translate.get(savedMessage).subscribe((message) => {
          this.popupsSrv.presentToast(message, 3000);
        });
      },
      complete: () => {
        if(flagLoading) {
          this.popupsSrv.dismissLoading();
        }
      }
    });
  }

  private saveAsyncDraft(menuSpec: protos.waiternow.common.MenuSpecProto): Observable<Object> {
    const observable = new Observable((observer: Observer<Object>) => {
      this.saveDraftMenuProto(menuSpec).subscribe((response) => {
        const answer = this.decodeSaveDraftOfMenuProto(response);
        if (this.isSaveDraftOfMenuProtoSuccess(answer)) {
          observer.next('Success');
        } else {
          this.processFailedAction(answer, 'SaveDraftError');
          observer.error('Error');
        }
        observer.complete();
      });
    });
    return observable;
  }

  public async removeDraft(menuSpec: protos.waiternow.common.MenuSpecProto): Promise<protos.waiternow.common.MenuSpecProto> {
    const loadingMsg = this.translate.instant('Loading');
    await this.popupsSrv.presentLoading(loadingMsg);

    try {
      const data = await firstValueFrom(this.removeDraftMenuProto(menuSpec));
      if (data) {
        const answer = this.decodeRemoveDraftOfMenuProto(data);
        if (this.isRemoveDraftOfMenuProtoSuccess(answer)) {
          return new protos.waiternow.common.MenuSpecProto();
        } else {
          this.processFailedAction(answer, 'DeleteDraftError');
        }
      }
      return menuSpec;
    } catch (err) {
        console.error(err);
        const message = await firstValueFrom(this.translate.get('DeleteDraftError'));
        this.popupsSrv.dismissLoading();
        this.popupsSrv.presentToast(message, 4000);
        return menuSpec;
    } finally {
      this.popupsSrv.dismissLoading();
    }
  }

  public async getFullMenu(): Promise<MenuLoadResult> {
    const messageLoading = this.translate.instant("Loading");
    await this.popupsSrv.presentLoading(messageLoading);

    try {
      const data = await firstValueFrom(this.getMenuProto());
      const answer = this.decodeGetDraftOfMenuProto(data);

      if (!this.isGetDraftOfMenuProtoSuccess(answer)) {
        return await this.handleErrorAndReturnEmpty('error_fetching_structured_menu');
      }

      const menuSpec = new protos.waiternow.common.MenuSpecProto(
        this.setMenuFromDraft(answer)
      );

      if (!this.isMenuSpecEmpty(menuSpec)) {
        const denormalizeData = await firstValueFrom(this.denormalizeMenu(menuSpec));
        const denormalizeAnswer = this.decodeDenormalizeMenuProtoResponse(denormalizeData);

        if (denormalizeAnswer && denormalizeAnswer.menu) {
          const fullMenu = new protos.waiternow.common.MenuProto(denormalizeAnswer.menu);

          return { menuSpec: menuSpec, menu: fullMenu };
        } else {
          return await this.handleErrorAndReturnEmpty('error_denormalizing_structured_menu', menuSpec);
        }
      }

      return {
        menuSpec: menuSpec,
        menu: new protos.waiternow.common.MenuProto()
      };

    } catch (err) {
      console.error('Error while loading menu: ', err);
      return await this.handleErrorAndReturnEmpty('error_fetching_structured_menu');
    } finally {
      this.popupsSrv.dismissLoading();
    }
  }

  private async handleErrorAndReturnEmpty(messageKey: string, existingSpec?: any): Promise<MenuLoadResult> {
    const text = await firstValueFrom(this.translate.get(messageKey));
    this.popupsSrv.presentToast(text, 2500, 'middle');

    return {
      menuSpec: existingSpec || new protos.waiternow.common.MenuSpecProto(),
      menu: new protos.waiternow.common.MenuProto()
    }
  }

  public publishDraft(menuSpec: protos.waiternow.common.MenuSpecProto): void {
    this.translate.get('Loading').subscribe((messageLoading) => {
      this.popupsSrv.presentLoading(messageLoading).then(() => {
        this.publishDraftMenuProto(menuSpec)
        .subscribe(resp => {
          const answer = this.decodePublishDraftOfMenuProto(resp);
          // console.log(answer);
          if (Boolean(this.isPublishDraftOfMenuProtoSuccess(answer))) {
            this.popupsSrv.dismissLoading();
            this.translate.get('MenuPublishedSuccessfully').subscribe((message) => {
              this.popupsSrv.presentToast(message, 3000);
            });
          } else {
            console.log('Publish error');
            this.processFailedAction(answer, 'SaveDraftError');
          }
        });
      });
    });
  }

  public async deleteMenu(menuSpec: protos.waiternow.common.MenuSpecProto) {
    const loadingMsg = this.translate.instant('Loading');
    await this.popupsSrv.presentLoading(loadingMsg);
    try {
      const data = await firstValueFrom(this.removeMenuProto());
      if (data) {
        const answer = this.decodeRemoveMenuProto(data);
        if (this.isRemoveMenuProtoSuccess(answer)) {
          return new protos.waiternow.common.MenuSpecProto();
        } else {
          this.processFailedAction(answer, 'DeleteMenuError');
        }
      }
      return menuSpec;
    } catch (err) {
        console.error(err);
        const message = await firstValueFrom(this.translate.get('DeleteMenuError'));
        this.popupsSrv.dismissLoading();
        this.popupsSrv.presentToast(message, 4000);
        return menuSpec;
    } finally {
      this.popupsSrv.dismissLoading();
    }
  }

  public getMenuEntryItemSelections(menuEntry: protos.waiternow.common.MenuProto.IMenuEntryProto): 
    Array<protos.waiternow.common.MenuProto.IItemSelectionProto> {
    if (menuEntry?.menuItem?.itemSelections) {
      return menuEntry?.menuItem?.itemSelections;
    } else if(menuEntry?.combo?.itemSelections) {
      return menuEntry?.combo?.itemSelections;
    }
    return [];
  }

  public isMenuSpecEmpty(menu: protos.waiternow.common.IMenuSpecProto): boolean {
    return (!menu.categories || menu.categories.length === 0)
        && (!menu.items || menu.items.length === 0)
        && (!menu.itemSelections || menu.itemSelections.length === 0)
        && (!menu.menuItems || menu.menuItems.length === 0)
        && (!menu.menuItemSelections || menu.menuItemSelections.length === 0)
        && (!menu.combos || menu.combos.length === 0)
        && (!menu.schedules || menu.schedules.length === 0);
  }

  public getAvailabilitySchedule(menuEntry: protos.waiternow.common.MenuProto.IMenuEntryProto): protos.waiternow.common.IAvailabilityScheduleProto | undefined {
    if (menuEntry?.menuItem && menuEntry?.menuItem?.availabilitySchedule) {
      return menuEntry?.menuItem?.availabilitySchedule;
    } else if (menuEntry?.combo && menuEntry?.combo?.availabilitySchedule) {
      return menuEntry?.combo?.availabilitySchedule;
    }
    return undefined;
  }

  public hasAvailabilitySchedule(menuEntry: protos.waiternow.common.MenuProto.IMenuEntryProto): boolean {
    if (menuEntry?.menuItem && menuEntry?.menuItem?.availabilitySchedule) {
      return true;
    } else if (menuEntry?.combo && menuEntry?.combo?.availabilitySchedule) {
      return true;
    }
    return false;
  }

  public hasMaxSelections(itemSelection: protos.waiternow.common.MenuProto.IItemSelectionProto): boolean {
    if (itemSelection.maxSelections) {
      return itemSelection.maxSelections > 0;
    }
    return false;
  }

  public isSingleSelection(itemSelection: protos.waiternow.common.MenuProto.IItemSelectionProto): boolean {
    return itemSelection.selectionType == protos.waiternow.common.SelectionType.SINGLE;
  }

  public isFirstItemSelectionInMenuEntry(
      itemSelection: protos.waiternow.common.MenuProto.IItemSelectionProto,
      menuEntry: protos.waiternow.common.MenuProto.IMenuEntryProto): boolean {
    const selections = this.getMenuEntryItemSelections(menuEntry);
    if (!selections || selections.length == 0) {
      return false;
    }
    return itemSelection.id == selections[0].id;
  }

  public isLastItemSelectionInMenuEntry(
      itemSelection: protos.waiternow.common.MenuProto.IItemSelectionProto,
      menuEntry: protos.waiternow.common.MenuProto.IMenuEntryProto): boolean {
    const selections = this.getMenuEntryItemSelections(menuEntry);
    if (!selections || selections.length == 0) {
      return false;
    }
    return itemSelection.id == selections[selections.length - 1].id;
  }

  public findDenormalizedMenuEntries(
    unfilteredMenu: protos.waiternow.common.IMenuProto,
    id: string
  ): Array<protos.waiternow.common.MenuProto.IMenuEntryProto> {
    const result: Array<protos.waiternow.common.MenuProto.IMenuEntryProto> = [];

    for (const menuEntry of unfilteredMenu.uncategorizedMenuEntries || []) {
      if (this.getMenuEntryId(menuEntry) == id) {
        result.push(menuEntry);
      }
    }

    for (const categoryLevel1 of unfilteredMenu.categories || []) {
      if (categoryLevel1.menuEntries) {
        for (const menuEntry of categoryLevel1.menuEntries) {
          if (this.getMenuEntryId(menuEntry) == id) {
            result.push(menuEntry);
            break;
          }
        }
      }
      if (categoryLevel1.categories) {
        for (const categoryLevel2 of categoryLevel1.categories) {
          if (categoryLevel2.menuEntries) {
            for (const menuEntry of categoryLevel2.menuEntries) {
              if (this.getMenuEntryId(menuEntry) == id) {
                result.push(menuEntry);
                break;
              }
            }
          }
          if (categoryLevel2.categories) {
            for (const categoryLevel3 of categoryLevel2.categories) {
              if (categoryLevel3.menuEntries) {
                for (const menuEntry of categoryLevel3.menuEntries) {
                  if (this.getMenuEntryId(menuEntry) == id) {
                    result.push(menuEntry);
                    break;
                  }
                }
              }
            }
          }
        }
      }
    }

    return result;
  }

  public applyMenuEntryUpdate(
    menuSpec: protos.waiternow.common.MenuSpecProto,
    unfilteredMenu: protos.waiternow.common.IMenuProto,
    menuEntry: protos.waiternow.common.MenuProto.IMenuEntryProto,
    menuItemSpecUpdater: (spec: protos.waiternow.common.MenuSpecProto.MenuItemProto) => void,
    comboSpecUpdater: (spec: protos.waiternow.common.MenuSpecProto.ComboProto) => void,
    menuItemUpdater: (item: protos.waiternow.common.MenuProto.MenuItemProto) => void,
    comboUpdater: (combo: protos.waiternow.common.MenuProto.ComboProto) => void
  ): void {

    // Updates menu spec
    if (menuEntry.menuItem) {
      for(const menuItemSpec of menuSpec.menuItems) {
        if (menuItemSpec.id == menuEntry.menuItem.id) {
          menuItemSpecUpdater(menuItemSpec as protos.waiternow.common.MenuSpecProto.MenuItemProto);
          break;
        }
      }
    } else if (menuEntry.combo) {
      for(const comboSpec of menuSpec.combos) {
        if (comboSpec.id == menuEntry.combo.id) {
          comboSpecUpdater(comboSpec as protos.waiternow.common.MenuSpecProto.ComboProto);
          break;
        }
      }
    }

    // Updates denormalized menu. Note that we don't directly update menuEntry becuase the same
    // menu entry may belong to multiple categories. So we update all instances.
    const menuEntries = this.findDenormalizedMenuEntries(unfilteredMenu, this.getMenuEntryId(menuEntry));
    for (const denormalizedMenuEntry of menuEntries) {
      if (denormalizedMenuEntry.menuItem) {
        menuItemUpdater(denormalizedMenuEntry.menuItem as protos.waiternow.common.MenuProto.MenuItemProto);
      } else if (denormalizedMenuEntry.combo) {
        comboUpdater(denormalizedMenuEntry.combo as protos.waiternow.common.MenuProto.ComboProto);
      }
    }
  }

  public getMenuEntryId(menuEntry: protos.waiternow.common.MenuProto.IMenuEntryProto): string {
    return MenuEditorUtil.getMenuEntryId(menuEntry);
  }

  private processFailedAction(answer: BaseProtoResponse, defailtMsgKey: string) {
    this.popupsSrv.dismissLoading();

    const status = answer?.operationStatus;
    const errorCode = status?.errorCode?.toLowerCase();
    
    if (!errorCode) {
      this.showToastAndDismissLoading(defailtMsgKey);
      return;
    }

    this.translate.get(errorCode).subscribe(translatedMsg => {
      const finalMessage = (translatedMsg === errorCode)
        ? this.translate.instant(defailtMsgKey)
        : translatedMsg;

      console.error(`Error code: ${errorCode}: ${status?.errorMessage}`);
      this.showToastAndDismissLoading(finalMessage);
    });
  }

  private async showToastAndDismissLoading(message: string) {
    this.popupsSrv.presentToast(message, 2500, 'middle').then(() => {
      this.popupsSrv.dismissLoading();
    });
  }

  public async saveNewVersion(
    menuSpec: protos.waiternow.common.MenuSpecProto,
    menu: protos.waiternow.common.IMenuProto
  ) {

      const menuCopy = await ProtoUtil.getDeepCopyFromMenuProto(menu);
      const menuSpecCopyAux = await ProtoUtil.getDeepCopyFromMenuSpecProto(menuSpec);
      const menuSpecCopy = new protos.waiternow.common.MenuSpecProto(menuSpecCopyAux.finalMenu);

      const newVersion = {
        timestamp: new Date(),
        menuSpec: menuSpecCopy,
        menu: menuCopy.finalMenu
      };
      // Get current state
      const currentHistory = this.menuHistory.value;
      // Create new versions array
      const updatedVersions = [newVersion, ...currentHistory.versions];

      // Emit new state
      this.menuHistory.next({
        activeIndex: 0,
        versions: updatedVersions
      });
      this.popupsSrv.dismissLoading();
  }

  public updatedActiveIndex(index: number) {
    console.log('new index: ', index);
    if (index !== undefined && index !== null) {
      this.menuHistory.next({
        ...this.menuHistory.value,
        activeIndex: index
      });
      this.timeTravelTrigger.next(index);
    }
  }

  public getMenuHistory(): Observable<any> {
    return this.menuHistory.asObservable();
  }

  public getTimeTravelEvents(): Observable<number> {
    return this.timeTravelTrigger.asObservable();
  }

  public reset(): void {
    this.menuHistory.next({
      activeIndex: 0,
      versions: []
    });
  }


  public getAllCategories(menuSpec: protos.waiternow.common.MenuSpecProto): Array<EditorCategory> {
    return MenuEditorUtil.getAllCategorySpecsAsEditorCategories(menuSpec);
  }

  public getMenuEntryCategoryIds(menuEntry: protos.waiternow.common.MenuProto.IMenuEntryProto, menuSpec: protos.waiternow.common.MenuSpecProto): Array<string> {
    return MenuEditorUtil.getMenuEntryCategoryIds(menuEntry, menuSpec);
  }

  public consolidateMenuEntryCategories(
    menuSpec: protos.waiternow.common.MenuSpecProto, 
    unfilteredMenu: protos.waiternow.common.IMenuProto, menuEntry : protos.waiternow.common.MenuProto.IMenuEntryProto, newMenuEntryCategories: Array<EditorCategory>
  ): void {
    if (newMenuEntryCategories.length == 0) {
      return;
    }

    const menuEntryId = this.getMenuEntryId(menuEntry);

    const currentMenuEntryCategoryIds: Array<string> = MenuEditorUtil.getMenuEntryCategoryIds(menuEntry, menuSpec);
    const newMenuEntryExistentCategoryIds: Array<string> = newMenuEntryCategories.filter(category => category.id ? true : false).map(category => Util.safeString(category.id));

    const existentCategoryIdsToAddToMenuItem: Array<string> = newMenuEntryExistentCategoryIds.filter(categoryId => !Util.arrayContains(categoryId, currentMenuEntryCategoryIds));
    const nonExistentCategoriesToAddToMenuItem: Array<EditorCategory> = newMenuEntryCategories.filter(category => !category.id ? true : false);
    const existentCategoryIdsToRemoveFromMenuEntry: Array<string> = currentMenuEntryCategoryIds.filter(categoryId => !Util.arrayContains(categoryId, newMenuEntryExistentCategoryIds));
    const categoryIdsToRemoveFromMenu: Array<string> = [];
    for (const potentialCategoryIdToRemoveFromMenu of existentCategoryIdsToRemoveFromMenuEntry) {
      let removeCategoryFromMenu = true;
      for (const menuItemSpec of menuSpec.menuItems || []) {
        if (menuItemSpec.id == menuEntryId) {
          continue;
        }
        if (Util.arrayContains(potentialCategoryIdToRemoveFromMenu, menuItemSpec.categoryIds)) {
          removeCategoryFromMenu = false;
          break;
        }
      }
      if (removeCategoryFromMenu) {
        for (const comboSpec of menuSpec.combos || []) {
          if (comboSpec.id == menuEntryId) {
            continue;
          }
          if (Util.arrayContains(potentialCategoryIdToRemoveFromMenu, comboSpec.categoryIds)) {
            removeCategoryFromMenu = false;
            break;
          }
        }
      }
      if (removeCategoryFromMenu) {
        categoryIdsToRemoveFromMenu.push(potentialCategoryIdToRemoveFromMenu);
      }
    }

    for (const category of nonExistentCategoriesToAddToMenuItem){
      category.id = this.generateCategoryId(menuSpec);
    }
    const newMenuEntryCategoryIds: Array<string> =[
      ...currentMenuEntryCategoryIds.filter(cateogyId => !Util.arrayContains(cateogyId, existentCategoryIdsToRemoveFromMenuEntry)),
      ...existentCategoryIdsToAddToMenuItem,
      ...nonExistentCategoriesToAddToMenuItem.map(category => Util.safeString(category.id))
    ];

    // TODO Remove
    /*
    console.log('currentMenuEntryCategoryIds:');
    console.log(currentMenuEntryCategoryIds);
    console.log('newMenuEntryExistentCategoryIds:');
    console.log(newMenuEntryExistentCategoryIds);
    console.log('existentCategoryIdsToAddToMenuItem:');
    console.log(existentCategoryIdsToAddToMenuItem);
    console.log('nonExistentCategoriesToAddToMenuItem:');
    console.log(nonExistentCategoriesToAddToMenuItem);
    console.log('existentCategoryIdsToRemoveFromMenuEntry:');
    console.log(existentCategoryIdsToRemoveFromMenuEntry);
    console.log('categoryIdsToRemoveFromMenu:');
    console.log(categoryIdsToRemoveFromMenu);
    console.log('newMenuEntryCategoryIds:');
    console.log(newMenuEntryCategoryIds);
    */

    this.applyMenuEntryUpdate(
      menuSpec,
      unfilteredMenu,
      menuEntry,
      menuItemSpec => {
        menuItemSpec.categoryIds = newMenuEntryCategoryIds;
      },
      comboSpec => {
        comboSpec.categoryIds = newMenuEntryCategoryIds;
      },
      menuItem => {
      },
      combo => {
      }
    );

    // --------
    // Deletes Categories From Menu
    // --------

    menuSpec.categories = menuSpec.categories.filter(categorySpec => !Util.arrayContains(categorySpec.id, categoryIdsToRemoveFromMenu));
    // TODO: Add support for category levels 2 and 3
    unfilteredMenu.categories = unfilteredMenu.categories?.filter(category => !Util.arrayContains(category.id, categoryIdsToRemoveFromMenu));

    // --------
    // Adds New Categories to Menu
    // --------

    for (const category of nonExistentCategoriesToAddToMenuItem) {
      const categorySpec = new protos.waiternow.common.MenuSpecProto.CategoryProto();
      categorySpec.id = Util.safeString(category.id);
      categorySpec.name = ProtoUtil.createTextProto(category.nameEn, category.nameEs);
      if (!menuSpec.categories) {
        menuSpec.categories = [];
      }
      menuSpec.categories.push(categorySpec);

      // TODO: Add support for category levels 2 and 3
      const categoryLevel1 = new protos.waiternow.common.MenuProto.CategoryLevel1Proto();
      categoryLevel1.id = Util.safeString(category.id);
      categoryLevel1.name = ProtoUtil.createTextProto(category.nameEn, category.nameEs);
      categoryLevel1.menuEntries = [];
      categoryLevel1.menuEntries.push(menuEntry);
      if (!unfilteredMenu.categories) {
        unfilteredMenu.categories = [];
      }
      unfilteredMenu.categories.push(categoryLevel1);
    }

    // --------
    // Removes the menu entry from the categories level 1
    // --------

    for (const cetegoryLevel1Id of existentCategoryIdsToRemoveFromMenuEntry) {
      for (const cetegoryLevel1 of unfilteredMenu.categories || []) {
        if (cetegoryLevel1Id == cetegoryLevel1.id) {
          cetegoryLevel1.menuEntries = cetegoryLevel1.menuEntries?.filter(categoryMenuEntry => this.getMenuEntryId(categoryMenuEntry) != menuEntryId);
          break;
        }
      }
    }

    // --------
    // Adds the menu entry to the new categories level 1
    // --------

    const menuItemSpecsIndex = new Map<string, number>();
    for (let i = 0; i < menuSpec.menuItems.length ; i++) {
      menuItemSpecsIndex.set(Util.safeString(menuSpec.menuItems[i].id), i);
    }
    for (const cetegoryLevel1Id of existentCategoryIdsToAddToMenuItem) {
      for (const cetegoryLevel1 of unfilteredMenu.categories || []) {
        if (cetegoryLevel1Id == cetegoryLevel1.id) {
          // We need to add thge menu entry in the position where the denormalizer would put it
          // The position is the order defined by menuSpec.menuItems
          const newCategoryLevel1MenuItems: Array<protos.waiternow.common.MenuProto.IMenuEntryProto> = [];
          let added = false;
          const menuEntryIndex: number = menuItemSpecsIndex.get(menuEntryId) || 0;
          for (const categoryMenuEntry of cetegoryLevel1.menuEntries || []) {
            const categoryMenuEntryIndex: number = menuItemSpecsIndex.get(this.getMenuEntryId(categoryMenuEntry)) || 0;
            if (!added && menuEntryIndex < categoryMenuEntryIndex) {
              newCategoryLevel1MenuItems.push(menuEntry);
              added = true;
            }
            newCategoryLevel1MenuItems.push(categoryMenuEntry);
          }
          if (!added) {
            newCategoryLevel1MenuItems.push(menuEntry);
          }
          cetegoryLevel1.menuEntries = newCategoryLevel1MenuItems;
          break;
        }
      }
    }
  }

  private generateCategoryId(menuSpec: protos.waiternow.common.MenuSpecProto): string {
    return this.generateId('category-id-', menuSpec.categories.map(category => category.id));
  }

  private generateId(idPrefix: string, currentIds: Array<string  | null | undefined>) : string {
    let count = 1;
    let id = idPrefix + (count++);
    while(Util.arrayContains(id, currentIds)) {
      id = idPrefix + (count++);
    }
    return id;
  }

  public applyItemSelectionUpdate(
      menuSpec: protos.waiternow.common.MenuSpecProto,
      unfilteredMenu: protos.waiternow.common.IMenuProto,
      itemSelection: protos.waiternow.common.MenuProto.IItemSelectionProto,
      itemSelectionSpecUpdater: Consumer<protos.waiternow.common.MenuSpecProto.IItemSelectionProto>,
      itemSelectionUpdater: Consumer<protos.waiternow.common.MenuProto.IItemSelectionProto>): void {
    // Updates menu spec
    for (const itemSlectionSpec of menuSpec.itemSelections) {
      if (itemSlectionSpec.id == itemSelection.id) {
        itemSelectionSpecUpdater(itemSlectionSpec as protos.waiternow.common.MenuSpecProto.ItemSelectionProto);
        break;
      }
    }

    // Updates denormalized menu. Note that we don't directly update item becuase the same
    // item selection may belong to multiple menu items. So we update all instances.

    for (const menuEntry of unfilteredMenu.uncategorizedMenuEntries || []) {
      this.applyItemSelectionUpdateForMenuEntry(itemSelection, menuEntry, itemSelectionUpdater);
    }

    for (const categoryLevel1 of unfilteredMenu.categories || []) {
      if (categoryLevel1.menuEntries) {
        for (const menuEntry of categoryLevel1.menuEntries) {
          this.applyItemSelectionUpdateForMenuEntry(itemSelection, menuEntry, itemSelectionUpdater);
        }
      }
      if (categoryLevel1.categories) {
        for (const categoryLevel2 of categoryLevel1.categories) {
          if (categoryLevel2.menuEntries) {
            for (const menuEntry of categoryLevel2.menuEntries) {
              this.applyItemSelectionUpdateForMenuEntry(itemSelection, menuEntry, itemSelectionUpdater);
            }
          }
          if (categoryLevel2.categories) {
            for (const categoryLevel3 of categoryLevel2.categories) {
              if (categoryLevel3.menuEntries) {
                for (const menuEntry of categoryLevel3.menuEntries) {
                  this.applyItemSelectionUpdateForMenuEntry(itemSelection, menuEntry, itemSelectionUpdater);
                }
              }
            }
          }
        }
      }
    }
  }

  private applyItemSelectionUpdateForMenuEntry(
      itemSelection: protos.waiternow.common.MenuProto.IItemSelectionProto,
      menuEntry: protos.waiternow.common.MenuProto.IMenuEntryProto,
      itemSelectionpdater: Consumer<protos.waiternow.common.MenuProto.ItemSelectionProto>): void {
    for (const _itemSelection of this.getMenuEntryItemSelections(menuEntry)) {
      if (_itemSelection.id == itemSelection.id) {
        itemSelectionpdater(_itemSelection as protos.waiternow.common.MenuProto.ItemSelectionProto);
      }
    }
  }

  public applyItemUpdate(
    menuSpec: protos.waiternow.common.MenuSpecProto,
    unfilteredMenu: protos.waiternow.common.IMenuProto,
    item: protos.waiternow.common.MenuProto.IItemProto,
    itemSpecUpdater: Consumer<protos.waiternow.common.MenuSpecProto.ItemProto>,
    itemUpdater: Consumer<protos.waiternow.common.MenuProto.ItemProto>): void {
    // Updates menu spec
    for (const itemSpec of menuSpec.items) {
      if (itemSpec.id == item.id) {
        itemSpecUpdater(itemSpec as protos.waiternow.common.MenuSpecProto.ItemProto);
        break;
      }
    }

    // Updates denormalized menu. Note that we don't directly update item becuase the same
    // item may belong to multiple selections. So we update all instances.

    for (const menuEntry of unfilteredMenu.uncategorizedMenuEntries || []) {
      this.applyItemUpdateForMenuEntry(menuSpec, unfilteredMenu, item, menuEntry, itemUpdater);
    }

    for (const categoryLevel1 of unfilteredMenu.categories || []) {
      if (categoryLevel1.menuEntries) {
        for (const menuEntry of categoryLevel1.menuEntries) {
          this.applyItemUpdateForMenuEntry(menuSpec, unfilteredMenu, item, menuEntry, itemUpdater);
        }
      }
      if (categoryLevel1.categories) {
        for (const categoryLevel2 of categoryLevel1.categories) {
          if (categoryLevel2.menuEntries) {
            for (const menuEntry of categoryLevel2.menuEntries) {
              this.applyItemUpdateForMenuEntry(menuSpec, unfilteredMenu, item, menuEntry, itemUpdater);
            }
          }
          if (categoryLevel2.categories) {
            for (const categoryLevel3 of categoryLevel2.categories) {
              if (categoryLevel3.menuEntries) {
                for (const menuEntry of categoryLevel3.menuEntries) {
                  this.applyItemUpdateForMenuEntry(menuSpec, unfilteredMenu, item, menuEntry, itemUpdater);
                }
              }
            }
          }
        }
      }
    }
  }

  private applyItemUpdateForMenuEntry(
      menuSpec: protos.waiternow.common.MenuSpecProto,
      unfilteredMenu: protos.waiternow.common.IMenuProto,
      item: protos.waiternow.common.MenuProto.IItemProto,
      menuEntry: protos.waiternow.common.MenuProto.IMenuEntryProto,
      itemUpdater: Consumer<protos.waiternow.common.MenuProto.ItemProto>): void {
    for (const itemSelection of this.getMenuEntryItemSelections(menuEntry)) {
      for (const _item of itemSelection.items || []) {
        if (_item.id == item.id) {
          itemUpdater(_item as protos.waiternow.common.MenuProto.ItemProto);
        }
      }
    }
    for (const _item of this.getMenuEntryRemovableIngredients(menuEntry)) {
      if (_item.id == item.id) {
        itemUpdater(_item as protos.waiternow.common.MenuProto.ItemProto);
      }
    }
  }

  public getMenuEntryRemovableIngredients(menuEntry: protos.waiternow.common.MenuProto.IMenuEntryProto): Array<protos.waiternow.common.MenuProto.IItemProto> {
    return MenuEditorUtil.getMenuEntryRemovableIngredients(menuEntry);
  }

  public getSelectionAppliesTo(menuSpec: protos.waiternow.common.MenuSpecProto, itemSelectionId: string): Array<protos.waiternow.common.ITextProto> {
    let itemSelectionTitle: protos.waiternow.common.ITextProto = new protos.waiternow.common.TextProto();
    let reusedInMenuItems: Array<protos.waiternow.common.ITextProto> = [];

    const itemSelectionSpec = MenuEditorUtil.findItemSelectionSpec(itemSelectionId, menuSpec);
    if (itemSelectionSpec) {
      for (const menuItemSpec of menuSpec.menuItems) {
        if (Util.arrayContains(itemSelectionId, menuItemSpec.itemSelectionIds)) {
          if (menuItemSpec.name) {
            reusedInMenuItems.push(menuItemSpec.name);
          }
        }
      }
    }
    return reusedInMenuItems;
  }

  public getAggregatedSelections(
    menuEntry: protos.waiternow.common.MenuProto.IMenuEntryProto | null | undefined
  ) {
    if (!menuEntry) return [];

    const uniqueSelectionMap = new Map<string, AggregatedSelection>();

    if (menuEntry.menuItem && menuEntry.menuItem?.itemSelections) {
      const parentId = Util.safeString(menuEntry.menuItem.id);
      const parentName = menuEntry.menuItem.name;

      menuEntry.menuItem.itemSelections.forEach((selection) => {
        if (selection.id) {
          uniqueSelectionMap.set(selection.id, {
            selection: selection,
            parentItems: [{ id: parentId, name: parentName }]
          });
        }
      });
    }

    if (menuEntry.combo && menuEntry.combo?.menuItems) {
      menuEntry.combo.menuItems.forEach((childMenuItem) => {
        const parentId = Util.safeString(childMenuItem.id);
        const parentName = childMenuItem.name;

        if (childMenuItem.itemSelections && Array.isArray(childMenuItem.itemSelections)) {
          childMenuItem.itemSelections.forEach((selection) => {

            if (selection.id) {
              if (uniqueSelectionMap.has(selection.id)) {

                const existing = uniqueSelectionMap.get(selection.id)!;
                if (!existing.parentItems.some(p => p.id === parentId)) {
                  existing.parentItems.push({ id: parentId, name: parentName });
                }

              } else {
                uniqueSelectionMap.set(selection.id, {
                  selection: selection,
                  parentItems: [{ id: parentId, name: parentName }]
                });
              }
              
            }

          });
        }
      });
    }
    return Array.from(uniqueSelectionMap.values());
  }

}
